import { Link, useNavigate } from 'react-router-dom';
import { Avatar, Badge, Button, Card, CardHeader, EmptyState, ErrorState, PageHeader, Skeleton } from '../../../components/ui';
import { useFetch } from '../../../hooks/useFetch';
import { labelOf } from '../../../utils/labels';
import { PAY_TYPES, PAYMENT_MODES } from '../../../constants/payments';
import { formatDate } from '../../../utils/date';
import { money } from '../../../utils/currency';
import { transactionService } from '../../transactions/services/transactionService';
import { payrollService } from '../services/payrollService';

/** Advances given to staff, what salary slips have recovered, and what is still pending. */
export default function StaffAdvanceList() {
  const navigate = useNavigate();
  const { data, error, reload } = useFetch(() => payrollService.advances(), []);
  const { data: recent } = useFetch(() => transactionService.list({ category: 'staff_advance', includeReversed: 'true', limit: 30 }).then((r) => r.data), []);
  const give = (userId?: string) => navigate(`/transactions/new?category=staff_advance${userId ? `&staffId=${userId}` : ''}&from=advances`);

  return (
    <>
      <PageHeader
        title="Staff advances"
        subtitle="Money given ahead of salary. It is recovered on the person's next salary slips."
        actions={
          <>
            <Button variant="outline" leftIcon="wallet" onClick={() => navigate('/payroll')}>Salary slips</Button>
            <Button leftIcon="plus" onClick={() => give()}>Give advance</Button>
          </>
        }
      />
      <div className="space-y-5">
        <Card className="overflow-hidden">
          <CardHeader title="Pending by person" action={data && <span className="tabular text-sm font-semibold text-sun-700">{money(data.total)} pending</span>} className="pb-4" />
          {error ? (
            <ErrorState error={error} onRetry={reload} />
          ) : !data ? (
            <div className="space-y-2 p-5">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-11" />)}</div>
          ) : data.rows.length === 0 ? (
            <EmptyState icon="users" title="No staff yet" message="Add workers in Team first." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-left text-sm">
                <thead className="border-y border-line bg-navy-50/40 text-[13px] text-ink-500">
                  <tr>
                    <th scope="col" className="px-5 py-3 font-medium">Staff</th>
                    <th scope="col" className="px-5 py-3 text-right font-medium">Advances given</th>
                    <th scope="col" className="px-5 py-3 text-right font-medium">Recovered</th>
                    <th scope="col" className="px-5 py-3 text-right font-medium">Pending</th>
                    <th scope="col" className="px-5 py-3"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody className="tabular divide-y divide-line">
                  {data.rows.map((r) => (
                    <tr key={r.user._id} className="hover:bg-navy-50/60">
                      <td className="px-5 py-3">
                        <span className="flex items-center gap-3">
                          <Avatar name={r.user.name} size={32} />
                          <span>
                            <span className="block font-medium text-navy-900">{r.user.name}</span>
                            <span className="block text-xs text-ink-400">{labelOf(PAY_TYPES, r.user.payType) || 'Not on payroll'}</span>
                          </span>
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right">{money(r.given)}</td>
                      <td className="px-5 py-3 text-right text-emerald-700">{money(r.recovered)}</td>
                      <td className="px-5 py-3 text-right font-semibold">{r.balance > 0 ? <span className="text-sun-700">{money(r.balance)}</span> : <span className="text-ink-300">-</span>}</td>
                      <td className="px-5 py-3 text-right">
                        <span className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => give(r.user._id)}>Give advance</Button>
                          {r.balance > 0 && r.user.payType && (
                            <Button size="sm" variant="ghost" onClick={() => navigate(`/payroll/new?userId=${r.user._id}`)}>Recover in slip</Button>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card className="overflow-hidden">
          <CardHeader title="Recent advances" className="pb-4" />
          {!recent ? (
            <div className="space-y-2 p-5">{[0, 1].map((i) => <Skeleton key={i} className="h-10" />)}</div>
          ) : recent.length === 0 ? (
            <p className="border-t border-line px-5 py-8 text-center text-sm text-ink-400">No advances given yet.</p>
          ) : (
            <ul className="divide-y divide-line border-t border-line">
              {recent.map((t) => (
                <li key={t._id}>
                  <Link to={`/transactions/${t._id}`} className="flex items-center gap-4 px-5 py-3 hover:bg-navy-50/60">
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-navy-900">{t.partyName}</span>
                      <span className="block text-xs text-ink-400">{[formatDate(t.date), labelOf(PAYMENT_MODES, t.mode), t.transactionNo, t.note].filter(Boolean).join(' · ')}</span>
                    </span>
                    {t.isReversed && <Badge tone="red">Reversed</Badge>}
                    {t.reversalOf && <Badge tone="yellow">Reversal</Badge>}
                    <span className={`tabular w-28 text-right text-sm font-semibold ${t.isReversed || t.reversalOf ? 'text-ink-400 line-through' : 'text-navy-900'}`}>{money(t.amount)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}
